import { Injectable } from "@angular/core";
import { ApplicationContextService } from "src/app/core/services/application-context.service";
import { AdmissionsService } from "src/app/core/services/admissions.service";
import { FilterService } from "src/app/core/services/filter.service";
import { LocalStorageService } from "src/app/core/services/local-storage.service";
import { buildWorksheetFilterSnapshotFromAdmissions } from "./worksheet-filter-snapshot-from-admissions.util";
import {
  applyEngageWorklistPersistedExtras,
  buildEngageWorklistPersistedExtras,
  engageWorklistFiltersActive,
  EngageWorklistPersistedFilterExtras,
} from "./worksheet-engage-filter-persist.util";
import { WorksheetFilterSnapshot } from "./worksheet-saved-views.model";

const STORAGE_KEY_PREFIX = "wa-clinic-worksheet-applied-filters";

export interface WorksheetAppliedFiltersPersistedState {
  version: 2;
  toolbarWorklistKey: string;
  engageSelectedView: "ListView" | "PipelineView";
  snapshot: WorksheetFilterSnapshot;
  extras?: EngageWorklistPersistedFilterExtras;
}

/**
 * Persists Engage worklist filters in `localStorage` (scoped per user + company).
 */
@Injectable({ providedIn: "root" })
export class WorksheetAppliedFiltersLocalStorageService {
  constructor(
    private readonly _localStorage: LocalStorageService,
    private readonly _appContext: ApplicationContextService,
    private readonly _filterService: FilterService
  ) {}

  load(): WorksheetAppliedFiltersPersistedState | null {
    const key = this.storageKey();
    if (!key) {
      return null;
    }
    const raw = this._localStorage.getItem<WorksheetAppliedFiltersPersistedState>(key);
    if (!raw?.snapshot) {
      return null;
    }
    const version = raw.version === 2 ? 2 : raw.version === 1 ? 1 : 0;
    if (version !== 1 && version !== 2) {
      return null;
    }
    const view = raw.engageSelectedView;
    if (view !== "ListView" && view !== "PipelineView") {
      return null;
    }
    return {
      version: 2,
      toolbarWorklistKey: String(raw.toolbarWorklistKey ?? "all").trim() || "all",
      engageSelectedView: view,
      snapshot: raw.snapshot,
      extras: version === 2 ? raw.extras : undefined,
    };
  }

  save(state: Omit<WorksheetAppliedFiltersPersistedState, "version">): void {
    const key = this.storageKey();
    if (!key) {
      return;
    }
    this._localStorage.setItem<WorksheetAppliedFiltersPersistedState>(key, {
      version: 2,
      toolbarWorklistKey: state.toolbarWorklistKey,
      engageSelectedView: state.engageSelectedView,
      snapshot: state.snapshot,
      extras: state.extras,
    });
  }

  clear(): void {
    const key = this.storageKey();
    if (!key) {
      return;
    }
    this._localStorage.removeItem(key);
  }

  /**
   * Write the live Engage worklist filter state to `localStorage`.
   * Call when any filter changes (sidebar, column headers, toolbar search, tags, …).
   */
  syncFromAdmissions(
    admissions: AdmissionsService,
    meta?: {
      toolbarWorklistKey?: string;
      engageSelectedView?: "ListView" | "PipelineView";
      /** When true (default), remove storage if no filters remain on the All worklist. */
      clearWhenEmpty?: boolean;
    }
  ): void {
    const existing = this.load();
    const toolbarKey =
      (meta?.toolbarWorklistKey ?? existing?.toolbarWorklistKey ?? "all").trim() || "all";
    const engageSelectedView =
      meta?.engageSelectedView ?? existing?.engageSelectedView ?? "ListView";
    const clearWhenEmpty = meta?.clearWhenEmpty ?? true;
    const filtersActive = engageWorklistFiltersActive(admissions, this._filterService);

    if (!filtersActive && toolbarKey === "all") {
      if (clearWhenEmpty) {
        this.clear();
      }
      return;
    }

    this.save({
      toolbarWorklistKey: toolbarKey,
      engageSelectedView,
      snapshot: buildWorksheetFilterSnapshotFromAdmissions(admissions),
      extras: buildEngageWorklistPersistedExtras(admissions, this._filterService),
    });
  }

  applyLoadedExtras(
    admissions: AdmissionsService,
    extras: EngageWorklistPersistedFilterExtras | null | undefined
  ): void {
    applyEngageWorklistPersistedExtras(admissions, this._filterService, extras);
  }

  private storageKey(): string | null {
    const slug = this.userCompanySlug();
    return slug ? `${STORAGE_KEY_PREFIX}::${slug}` : null;
  }

  private userCompanySlug(): string | null {
    const user = this._appContext.contextSnapshot?.currentlyLoggedInUser;
    if (!user?.id) {
      return null;
    }
    const cid = String(user.companyId ?? "").trim() || "default";
    const safe = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, "_");
    return `${safe(user.id)}_${safe(cid)}`;
  }
}
